import React, { useState, useEffect, useRef } from 'react';
import { FaUser, FaEdit, FaKey, FaSave, FaTimes, FaCamera, FaIdCard } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Component for parents to view and edit their profile settings
 */
const ParentProfile = () => {
  const { user, updateUserData } = useAuth();
  const { showSuccess, showError } = useToast();
  const fileInputRef = useRef(null);
  
  // State for profile data
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    country: '',
    profileImage: ''
  });
  
  // State for password change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Component state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Fetch profile data on component mount
  useEffect(() => {
    fetchProfileData();
  }, []);
  
  // Fetch profile data from API
  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/parents/profile');
      
      // Set profile data
      setProfileData({
        firstName: response.data.data.firstName || '',
        lastName: response.data.data.lastName || '',
        email: response.data.data.email || '',
        phoneNumber: response.data.data.phoneNumber || '',
        address: response.data.data.address || '',
        city: response.data.data.city || '',
        country: response.data.data.country || '',
        profileImage: response.data.data.profileImage || ''
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      showError('Failed to load profile data');
      setLoading(false);
    }
  };
  
  // Handle profile form field changes
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    
    setProfileData({
      ...profileData,
      [name]: value
    });
    
    // Clear error for this field
    setProfileErrors({
      ...profileErrors,
      [name]: undefined
    });
  };
  
  // Handle password form field changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    
    setPasswordData({
      ...passwordData,
      [name]: value
    });
    
    // Clear error for this field
    setPasswordErrors({
      ...passwordErrors,
      [name]: undefined
    });
  };
  
  // Handle profile picture change
  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      showError('Please upload a JPG or PNG image');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('Image must be less than 5MB');
      return;
    }
    
    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Update form data with file
    setProfileData({
      ...profileData,
      profileImageFile: file
    });
  };
  
  // Handle profile picture click to open file dialog
  const handleProfilePictureClick = () => {
    if (editMode) {
      fileInputRef.current.click();
    }
  };
  
  // Validate profile form
  const validateProfileForm = () => {
    const errors = {};
    
    if (!profileData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!profileData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!profileData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(profileData.email)) {
      errors.email = 'Email is invalid';
    }
    
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Validate password form
  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    
    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Confirm password is required';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle profile form submission
  const handleProfileSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!validateProfileForm()) {
      return;
    }
    
    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('firstName', profileData.firstName);
      formData.append('lastName', profileData.lastName);
      formData.append('email', profileData.email);
      formData.append('phoneNumber', profileData.phoneNumber);
      formData.append('address', profileData.address);
      formData.append('city', profileData.city);
      formData.append('country', profileData.country);
      
      if (profileData.profileImageFile) {
        formData.append('profileImage', profileData.profileImageFile);
      }
      
      const response = await api.put('/parents/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update the user data in context
      if (response.data.success && updateUserData) {
        updateUserData(response.data.data);
      }
      
      showSuccess('Profile updated successfully');
      setEditMode(false);
      setPreviewImage(null);
      
      // Refresh profile data
      fetchProfileData();
    } catch (error) {
      console.error('Error updating profile:', error);
      showError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle password form submission
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    try {
      setChangingPassword(true);
      
      await api.put('/auth/updatepassword', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      showSuccess('Password updated successfully');
      
      // Reset password form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      showError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditMode(false);
    setPreviewImage(null);
    fetchProfileData(); // Revert to saved data
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <FaUser className="mr-2 text-primary" /> Parent Profile
      </h1>
      
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Picture Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-soft p-6">
              <div className="flex flex-col items-center">
                <div 
                  className={`relative w-40 h-40 rounded-full overflow-hidden mb-4 ${editMode ? 'cursor-pointer' : ''}`}
                  onClick={handleProfilePictureClick}
                >
                  {editMode && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <FaCamera className="text-white text-3xl" />
                    </div>
                  )}
                  <img 
                    src={previewImage || profileData.profileImage || 'https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/user-avatar_ssixtt.png'} 
                    alt={`${profileData.firstName} ${profileData.lastName}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleProfilePictureChange}
                  accept="image/jpeg, image/png"
                  className="hidden"
                />
                <h2 className="text-xl font-semibold mb-1">
                  {profileData.firstName} {profileData.lastName}
                </h2>
                <p className="text-gray-600 mb-2">{profileData.email}</p>
                
                {/* Edit/Cancel/Save Button */}
                {!editMode ? (
                  <button
                    type="button"
                    onClick={() => setEditMode(true)}
                    className="mt-3 flex items-center px-4 py-2 bg-primary border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-dark"
                  >
                    <FaEdit className="mr-2" /> Edit Profile
                  </button>
                ) : (
                  <div className="flex space-x-3 mt-3">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                      disabled={saving}
                    >
                      <FaTimes className="mr-2" /> Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleProfileSubmit}
                      className="flex items-center px-4 py-2 bg-primary border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-dark"
                      disabled={saving}
                    >
                      {saving ? <LoadingSpinner size="sm" className="mr-2" /> : <FaSave className="mr-2" />}
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Profile Information */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-soft p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaIdCard className="mr-2 text-primary" /> Personal Information
              </h2>
              
              <form onSubmit={handleProfileSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {/* First Name */}
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className={`form-input w-full ${profileErrors.firstName ? 'border-red-500' : ''}`}
                    />
                    {profileErrors.firstName && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.firstName}</p>
                    )}
                  </div>
                  
                  {/* Last Name */}
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className={`form-input w-full ${profileErrors.lastName ? 'border-red-500' : ''}`}
                    />
                    {profileErrors.lastName && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.lastName}</p>
                    )}
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className={`form-input w-full ${profileErrors.email ? 'border-red-500' : ''}`}
                    />
                    {profileErrors.email && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.email}</p>
                    )}
                  </div>
                  
                  {/* Phone */}
                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={profileData.phoneNumber}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      placeholder="e.g. 555-123-4567"
                      className="form-input w-full"
                    />
                  </div>
                </div>
                
                <h3 className="text-lg font-medium mb-3 flex items-center mt-6">
                  <FaIdCard className="mr-2 text-primary" /> Address Information
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Address */}
                  <div className="sm:col-span-2">
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={profileData.address}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      placeholder="Street address"
                      className="form-input w-full"
                    />
                  </div>
                  
                  {/* City */}
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={profileData.city}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className="form-input w-full"
                    />
                  </div>
                  
                  {/* Country */}
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={profileData.country}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className="form-input w-full"
                    />
                  </div>
                </div>
              </form>
            </div>
            
            {/* Password Change Form */}
            <div className="bg-white rounded-lg shadow-soft p-6 mt-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaKey className="mr-2 text-primary" /> Change Password
              </h2>
              
              <form onSubmit={handlePasswordSubmit}>
                <div className="grid grid-cols-1 gap-4">
                  {/* Current Password */}
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      disabled={changingPassword}
                      className={`form-input w-full ${passwordErrors.currentPassword ? 'border-red-500' : ''}`}
                    />
                    {passwordErrors.currentPassword && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.currentPassword}</p>
                    )}
                  </div>
                  
                  {/* New Password */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      disabled={changingPassword}
                      className={`form-input w-full ${passwordErrors.newPassword ? 'border-red-500' : ''}`}
                    />
                    {passwordErrors.newPassword && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.newPassword}</p>
                    )}
                  </div>
                  
                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      disabled={changingPassword}
                      className={`form-input w-full ${passwordErrors.confirmPassword ? 'border-red-500' : ''}`}
                    />
                    {passwordErrors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.confirmPassword}</p>
                    )}
                  </div>
                </div>
                
                <div className="mt-6">
                  <button
                    type="submit"
                    className="flex items-center px-4 py-2 bg-primary border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-dark"
                    disabled={changingPassword}
                  >
                    {changingPassword ? <LoadingSpinner size="sm" className="mr-2" /> : <FaKey className="mr-2" />}
                    Change Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentProfile; 